import { useMemo, useState } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import { AnalyticsChart } from './AnalyticsChart';
import {
  buildConstructionOption,
  buildResourceOption,
  buildTrendOption,
  type TrendDefinition,
  type TrendValueMode,
} from './analysis-chart-options';
import type { AnalysisMode, AnalysisSessionDataset, AnalysisTab } from './analysis-types';
import { EventOccurrenceDetails } from './EventOccurrencePresentation';
import { findProperty, formatDynamicValue, humanize, readObject } from './format';
import { SessionEventTimeline } from './SessionEventTimeline';
import type { AnalyticsEventRecord } from './types';

type DomainMetric = [label: string, value: number | string, detail: string];
const COLONY_METRICS: TrendDefinition[] = [
  { key: 'population', label: 'Population', color: '#86a8ff', read: readPopulation },
  { key: 'housing_capacity', label: 'Housing capacity', color: '#72d7ae', read: readHousingCapacity },
  { key: 'prosperity', label: 'Prosperity', color: '#f0c078', read: readProsperity },
  { key: 'housed_population', label: 'Housed', color: '#9f8eff', read: readHousedPopulation },
  { key: 'homeless_population', label: 'Homeless', color: '#ef8d93', read: readHomelessPopulation },
  { key: 'unemployed_population', label: 'Unemployed', color: '#d696ff', read: readUnemployedPopulation },
];
const ECONOMY_METRICS: TrendDefinition[] = [
  { key: 'money_balance', label: 'Balance', color: '#86a8ff', read: readMoneyBalance },
  { key: 'projected_daily_tax', label: 'Daily tax', color: '#72d7ae', read: readProjectedTax },
  { key: 'projected_daily_upkeep', label: 'Daily upkeep', color: '#ef8d93', read: readProjectedUpkeep },
  { key: 'projected_daily_net', label: 'Daily net', color: '#f0c078', read: readProjectedNet },
];
const LOGISTICS_METRICS: TrendDefinition[] = [
  { key: 'active_logistics_tasks', label: 'Active tasks', color: '#86a8ff', read: readActiveTasks },
  { key: 'economy_transfer_attempts', label: 'Transfer attempts', color: '#f0c078', read: readTransferAttempts },
  { key: 'economy_transfer_successes', label: 'Transfer successes', color: '#72d7ae', read: readTransferSuccesses },
  { key: 'transfer_success_rate', label: 'Success rate', color: '#d696ff', read: readTransferSuccessRate },
];
const CONSTRUCTION_EVENT_ORDER = [
  'building_placement_started',
  'building_placed',
  'building_completed',
  'building_destroyed',
];

export function AnalysisDomainView(props: {
  tab: AnalysisTab;
  mode: AnalysisMode;
  datasets: AnalysisSessionDataset[];
  eventLookup: ReadonlyMap<string, AnalyticsEventRecord>;
  selectedEvent: AnalyticsEventRecord | null;
  onSelectEvent: (event: AnalyticsEventRecord) => void;
}): React.JSX.Element {
  switch (props.tab) {
    case 'colony': return <ColonyView {...props} />;
    case 'economy': return <EconomyView {...props} />;
    case 'construction': return <ConstructionView {...props} />;
    case 'logistics': return <LogisticsView {...props} />;
  }
}

function ColonyView(props: DomainViewProps): React.JSX.Element {
  const [metricKey, setMetricKey] = useState('population');
  const [valueMode, setValueMode] = useState<TrendValueMode>('absolute');
  const snapshots = useMemo(() => selectSnapshotDatasets(props.datasets), [props.datasets]);
  const availableMetrics = useMemo(() => selectAvailableMetrics(snapshots, COLONY_METRICS), [snapshots]);
  const effectiveMetricKey = readEffectiveMetricKey(metricKey, availableMetrics);
  const comparison = props.mode === 'comparison';
  const chart = useMemo(
    () => buildTrendOption(snapshots, availableMetrics, comparison, effectiveMetricKey, valueMode),
    [availableMetrics, comparison, effectiveMetricKey, snapshots, valueMode],
  );
  const metrics = buildLatestMetrics(snapshots, availableMetrics.slice(0, 4), comparison);
  return <DomainLayout title="Colony trajectory" subtitle="Population, housing, and prosperity by session elapsed time" empty={countEvents(snapshots) === 0 || availableMetrics.length === 0} emptyText="The selected sessions contain no compatible colony snapshot metrics." metrics={metrics} chart={chart} controls={<TrendControls metrics={availableMetrics} comparison={comparison} metricKey={effectiveMetricKey} valueMode={valueMode} onMetricChange={setMetricKey} onValueModeChange={setValueMode} />} side={<DynamicSnapshotPanel title="Latest building mix" propertyKey="building_counts" datasets={snapshots} onSelectEvent={props.onSelectEvent} />} eventLookup={props.eventLookup} selectedEvent={props.selectedEvent} onSelectEvent={props.onSelectEvent} />;
}

function EconomyView(props: DomainViewProps): React.JSX.Element {
  const [metricKey, setMetricKey] = useState('money_balance');
  const [valueMode, setValueMode] = useState<TrendValueMode>('absolute');
  const snapshots = useMemo(() => selectSnapshotDatasets(props.datasets), [props.datasets]);
  const availableMetrics = useMemo(() => selectAvailableMetrics(snapshots, ECONOMY_METRICS), [snapshots]);
  const effectiveMetricKey = readEffectiveMetricKey(metricKey, availableMetrics);
  const comparison = props.mode === 'comparison';
  const chart = useMemo(
    () => buildTrendOption(snapshots, availableMetrics, comparison, effectiveMetricKey, valueMode),
    [availableMetrics, comparison, effectiveMetricKey, snapshots, valueMode],
  );
  const metrics = buildLatestMetrics(snapshots, availableMetrics, comparison);
  return <DomainLayout title="Economy pulse" subtitle="Balance and projected daily flow by session elapsed time" empty={countEvents(snapshots) === 0 || availableMetrics.length === 0} emptyText="The selected sessions contain no compatible economy snapshot metrics." metrics={metrics} chart={chart} controls={<TrendControls metrics={availableMetrics} comparison={comparison} metricKey={effectiveMetricKey} valueMode={valueMode} onMetricChange={setMetricKey} onValueModeChange={setValueMode} />} side={<ResourcePanel datasets={snapshots} eventLookup={props.eventLookup} onSelectEvent={props.onSelectEvent} />} eventLookup={props.eventLookup} selectedEvent={props.selectedEvent} onSelectEvent={props.onSelectEvent} />;
}

function LogisticsView(props: DomainViewProps): React.JSX.Element {
  const [metricKey, setMetricKey] = useState('active_logistics_tasks');
  const [valueMode, setValueMode] = useState<TrendValueMode>('absolute');
  const snapshots = useMemo(() => selectSnapshotDatasets(props.datasets), [props.datasets]);
  const availableMetrics = useMemo(() => selectAvailableMetrics(snapshots, LOGISTICS_METRICS), [snapshots]);
  const effectiveMetricKey = readEffectiveMetricKey(metricKey, availableMetrics);
  const comparison = props.mode === 'comparison';
  const chart = useMemo(
    () => buildTrendOption(snapshots, availableMetrics, comparison, effectiveMetricKey, valueMode),
    [availableMetrics, comparison, effectiveMetricKey, snapshots, valueMode],
  );
  const metrics = buildLatestMetrics(snapshots, availableMetrics, comparison);
  return <DomainLayout title="Logistics health" subtitle="Workload and transfer outcomes by session elapsed time" empty={countEvents(snapshots) === 0 || availableMetrics.length === 0} emptyText="The selected sessions contain no compatible logistics snapshot metrics." metrics={metrics} chart={chart} controls={<TrendControls metrics={availableMetrics} comparison={comparison} metricKey={effectiveMetricKey} valueMode={valueMode} onMetricChange={setMetricKey} onValueModeChange={setValueMode} />} side={<DynamicSnapshotPanel title="Latest unit activity" propertyKey="unit_activity_counts" datasets={snapshots} onSelectEvent={props.onSelectEvent} />} eventLookup={props.eventLookup} selectedEvent={props.selectedEvent} onSelectEvent={props.onSelectEvent} />;
}

function ConstructionView(props: DomainViewProps): React.JSX.Element {
  const construction = useMemo(() => selectConstructionDatasets(props.datasets), [props.datasets]);
  const eventKeys = useMemo(() => collectConstructionEventKeys(construction), [construction]);
  const chart = useMemo(() => buildConstructionOption(construction, eventKeys), [construction, eventKeys]);
  const totals = countConstructionEvents(construction);
  const metrics: DomainMetric[] = [
    ['Placement starts', totals.building_placement_started ?? 0, describeSelectedSessions(construction.length)],
    ['Placed', totals.building_placed ?? 0, describeSelectedSessions(construction.length)],
    ['Completed', totals.building_completed ?? 0, describeSelectedSessions(construction.length)],
    ['Destroyed', totals.building_destroyed ?? 0, describeSelectedSessions(construction.length)],
  ];
  return <DomainLayout title="Construction behavior" subtitle="Scoped counts stay separate for every selected session" empty={countEvents(construction) === 0} emptyText="The selected sessions contain no construction events." metrics={metrics} chart={chart} side={<ConstructionTimelinePanel datasets={construction} />} eventLookup={props.eventLookup} selectedEvent={props.selectedEvent} onSelectEvent={props.onSelectEvent} />;
}

interface DomainViewProps {
  mode: AnalysisMode;
  datasets: AnalysisSessionDataset[];
  eventLookup: ReadonlyMap<string, AnalyticsEventRecord>;
  selectedEvent: AnalyticsEventRecord | null;
  onSelectEvent: (event: AnalyticsEventRecord) => void;
}

function DomainLayout(props: {
  title: string;
  subtitle: string;
  empty: boolean;
  emptyText: string;
  metrics: DomainMetric[];
  chart: EChartsCoreOption;
  controls?: React.ReactNode;
  side: React.ReactNode;
  eventLookup: ReadonlyMap<string, AnalyticsEventRecord>;
  selectedEvent: AnalyticsEventRecord | null;
  onSelectEvent: (event: AnalyticsEventRecord) => void;
}): React.JSX.Element {
  return <section className="analysis-domain">
    <section className="metric-grid domain-metrics">{props.metrics.map(renderDomainMetric)}</section>
    <section className="domain-grid">
      <article className="surface domain-chart">
        <div className="surface-heading"><div><span className="eyebrow">Scoped trend</span><h2>{props.title}</h2></div><small>{props.subtitle}</small></div>
        {props.controls}
        {props.empty ? <div className="empty">{props.emptyText}</div> : <AnalyticsChart option={props.chart} eventLookup={props.eventLookup} onEventClick={props.onSelectEvent} />}
      </article>
      {props.side}
    </section>
    {props.selectedEvent !== null && <EventOccurrenceDetails event={props.selectedEvent} variant="panel" />}
  </section>;
}

function TrendControls(props: {
  metrics: TrendDefinition[];
  comparison: boolean;
  metricKey: string;
  valueMode: TrendValueMode;
  onMetricChange: (key: string) => void;
  onValueModeChange: (mode: TrendValueMode) => void;
}): React.JSX.Element {
  if (!props.comparison) {
    return <div className="domain-controls"><span>Click any point to inspect its source snapshot.</span></div>;
  }
  function changeMetric(event: React.ChangeEvent<HTMLSelectElement>): void { props.onMetricChange(event.target.value); }
  function showAbsolute(): void { props.onValueModeChange('absolute'); }
  function showDelta(): void { props.onValueModeChange('delta'); }
  return <div className="domain-controls">
    <label>Metric<select value={props.metricKey} onChange={changeMetric}>{props.metrics.map(renderTrendOption)}</select></label>
    <div className="analysis-value-toggle"><button className={props.valueMode === 'absolute' ? 'active' : ''} onClick={showAbsolute}>Absolute</button><button className={props.valueMode === 'delta' ? 'active' : ''} onClick={showDelta}>Change from start</button></div>
    <span>Click any point to inspect its source snapshot.</span>
  </div>;
}

function DynamicSnapshotPanel(props: {
  title: string;
  propertyKey: string;
  datasets: AnalysisSessionDataset[];
  onSelectEvent: (event: AnalyticsEventRecord) => void;
}): React.JSX.Element {
  return <article className="surface domain-table"><div className="surface-heading"><div><span className="eyebrow">Snapshot</span><h2>{props.title}</h2></div></div>
    <div className="snapshot-comparison-list">{props.datasets.map(renderDataset)}</div>
    {countEvents(props.datasets) === 0 && <div className="empty compact">No snapshot details yet.</div>}
  </article>;

  function renderDataset(dataset: AnalysisSessionDataset): React.JSX.Element {
    const latest = dataset.events.at(-1);
    const value = readObject(latest, props.propertyKey);
    function inspectSnapshot(): void { if (latest !== undefined) props.onSelectEvent(latest); }
    return <section className="snapshot-comparison" key={dataset.summary.sessionId}>
      <div><strong>Session #{dataset.summary.sessionIndex}</strong><button onClick={inspectSnapshot} disabled={latest === undefined}>Inspect snapshot</button></div>
      <div className="rank-list">{Object.entries(value).sort(sortDynamicValues).map(renderDynamicValue)}</div>
      {Object.keys(value).length === 0 && <small>No values recorded.</small>}
    </section>;
  }
}

function ResourcePanel(props: {
  datasets: AnalysisSessionDataset[];
  eventLookup: ReadonlyMap<string, AnalyticsEventRecord>;
  onSelectEvent: (event: AnalyticsEventRecord) => void;
}): React.JSX.Element {
  const resourceKeys = useMemo(() => collectResourceKeys(props.datasets), [props.datasets]);
  const [requestedResourceKey, setRequestedResourceKey] = useState('');
  const resourceKey = resourceKeys.includes(requestedResourceKey) ? requestedResourceKey : resourceKeys[0] ?? '';
  const chart = useMemo(() => buildResourceOption(props.datasets, resourceKey), [props.datasets, resourceKey]);
  function changeResource(event: React.ChangeEvent<HTMLSelectElement>): void { setRequestedResourceKey(event.target.value); }
  return <article className="surface domain-table resource-panel">
    <div className="surface-heading"><div><span className="eyebrow">Resources</span><h2>Stored resource trend</h2></div><strong>{resourceKeys.length}</strong></div>
    {resourceKeys.length > 0 && <label>Resource<select value={resourceKey} onChange={changeResource}>{resourceKeys.map(renderResourceOption)}</select></label>}
    {resourceKey === '' ? <div className="empty compact">No numeric resource values were recorded.</div> : <AnalyticsChart option={chart} height={220} eventLookup={props.eventLookup} onEventClick={props.onSelectEvent} />}
    {resourceKey !== '' && <div className="resource-latest-list">{props.datasets.map(renderLatest)}</div>}
  </article>;

  function renderLatest(dataset: AnalysisSessionDataset): React.JSX.Element {
    const latest = dataset.events.at(-1);
    return <div key={dataset.summary.sessionId}><span>Session #{dataset.summary.sessionIndex}</span><strong>{formatOptionalNumber(readResourceValue(latest, resourceKey))}</strong></div>;
  }
}

function ConstructionTimelinePanel(props: { datasets: AnalysisSessionDataset[] }): React.JSX.Element {
  return <article className="surface domain-table construction-timeline-panel">
    <div className="surface-heading"><div><span className="eyebrow">Occurrences</span><h2>Construction timelines</h2></div><strong>{countEvents(props.datasets)}</strong></div>
    {props.datasets.map(renderTimeline)}
    {countEvents(props.datasets) === 0 && <div className="empty compact">No construction occurrences yet.</div>}
  </article>;
}

function renderTimeline(dataset: AnalysisSessionDataset): React.JSX.Element {
  return <details className="construction-session-timeline" key={dataset.summary.sessionId} open>
    <summary>Session #{dataset.summary.sessionIndex} · {dataset.events.length} events</summary>
    <SessionEventTimeline events={dataset.events} />
  </details>;
}

function buildLatestMetrics(datasets: AnalysisSessionDataset[], definitions: TrendDefinition[], comparison: boolean): DomainMetric[] {
  const metrics: DomainMetric[] = [];
  for (const definition of definitions) {
    const latestValues: number[] = [];
    for (const dataset of datasets) {
      const latest = dataset.events.at(-1);
      const value = latest === undefined ? null : definition.read(latest);
      if (value !== null) latestValues.push(value);
    }
    if (comparison) {
      metrics.push([definition.label, formatRange(latestValues), `latest values across ${latestValues.length} sessions`]);
    } else {
      metrics.push([definition.label, latestValues[0] ?? '—', 'latest snapshot in this session']);
    }
  }
  return metrics.slice(0, 4);
}

function selectSnapshotDatasets(datasets: AnalysisSessionDataset[]): AnalysisSessionDataset[] {
  return datasets.map(selectSnapshotDataset);
}

function selectSnapshotDataset(dataset: AnalysisSessionDataset): AnalysisSessionDataset {
  return { summary: dataset.summary, events: dataset.events.filter(isColonySnapshot) };
}

function selectConstructionDatasets(datasets: AnalysisSessionDataset[]): AnalysisSessionDataset[] {
  return datasets.map(selectConstructionDataset);
}

function selectConstructionDataset(dataset: AnalysisSessionDataset): AnalysisSessionDataset {
  return { summary: dataset.summary, events: dataset.events.filter(isConstructionEvent) };
}

function collectConstructionEventKeys(datasets: AnalysisSessionDataset[]): string[] {
  const keys = new Set<string>();
  for (const key of CONSTRUCTION_EVENT_ORDER) keys.add(key);
  for (const dataset of datasets) for (const event of dataset.events) keys.add(event.eventKey);
  return [...keys];
}

function countConstructionEvents(datasets: AnalysisSessionDataset[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const dataset of datasets) {
    for (const event of dataset.events) counts[event.eventKey] = (counts[event.eventKey] ?? 0) + 1;
  }
  return counts;
}

function collectResourceKeys(datasets: AnalysisSessionDataset[]): string[] {
  const keys = new Set<string>();
  for (const dataset of datasets) {
    for (const event of dataset.events) {
      const resources = readObject(event, 'resources');
      for (const [key, value] of Object.entries(resources)) if (typeof value === 'number' && Number.isFinite(value)) keys.add(key);
    }
  }
  return [...keys].sort();
}

function selectAvailableMetrics(datasets: AnalysisSessionDataset[], metrics: TrendDefinition[]): TrendDefinition[] {
  const available = [];
  for (const metric of metrics) {
    if (datasets.some((dataset) => dataset.events.some((event) => hasMetric(event, metric)))) available.push(metric);
  }
  return available;
}

function hasMetric(event: AnalyticsEventRecord, metric: TrendDefinition): boolean {
  const propertyKey = metric.key === 'transfer_success_rate' ? 'economy_transfer_attempts' : metric.key;
  const value = findProperty(event, propertyKey)?.value;
  return typeof value === 'number' && Number.isFinite(value);
}

function readEffectiveMetricKey(requested: string, metrics: TrendDefinition[]): string {
  return metrics.some((metric) => metric.key === requested) ? requested : metrics[0]?.key ?? '';
}

function renderDomainMetric(metric: DomainMetric): React.JSX.Element {
  const [label, value, detail] = metric;
  return <article className="metric" key={label}><span>{label}</span><strong>{typeof value === 'number' ? formatNumber(value) : value}</strong><small>{detail}</small></article>;
}

function renderTrendOption(metric: TrendDefinition): React.JSX.Element { return <option key={metric.key} value={metric.key}>{metric.label}</option>; }
function renderResourceOption(key: string): React.JSX.Element { return <option key={key} value={key}>{humanize(key)}</option>; }
function renderDynamicValue(entry: [string, unknown]): React.JSX.Element { return <div key={entry[0]}><span>{humanize(entry[0])}</span><strong>{formatDynamicValue(entry[1])}</strong></div>; }
function readResourceValue(event: AnalyticsEventRecord | undefined, key: string): number | null { const value = readObject(event, 'resources')[key]; return typeof value === 'number' && Number.isFinite(value) ? value : null; }
function countEvents(datasets: AnalysisSessionDataset[]): number { let total = 0; for (const dataset of datasets) total += dataset.events.length; return total; }
function describeSelectedSessions(count: number): string { return count === 1 ? 'this selected session' : `across ${count} selected sessions`; }
function sortDynamicValues(left: [string, unknown], right: [string, unknown]): number { return left[0].localeCompare(right[0]); }
function isColonySnapshot(event: AnalyticsEventRecord): boolean { return event.eventKey === 'colony_snapshot'; }
function isConstructionEvent(event: AnalyticsEventRecord): boolean { return event.categoryKey === 'construction'; }
function formatRange(values: number[]): string { if (values.length === 0) return '—'; const minimum = Math.min(...values); const maximum = Math.max(...values); return minimum === maximum ? formatNumber(minimum) : `${formatNumber(minimum)}–${formatNumber(maximum)}`; }
function formatNumber(value: number): string { return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value); }
function formatOptionalNumber(value: number | null): string { return value === null ? '—' : formatNumber(value); }

function readPopulation(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'population'); }
function readHousingCapacity(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'housing_capacity'); }
function readProsperity(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'prosperity'); }
function readHousedPopulation(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'housed_population'); }
function readHomelessPopulation(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'homeless_population'); }
function readUnemployedPopulation(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'unemployed_population'); }
function readMoneyBalance(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'money_balance'); }
function readProjectedTax(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'projected_daily_tax'); }
function readProjectedUpkeep(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'projected_daily_upkeep'); }
function readProjectedNet(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'projected_daily_net'); }
function readActiveTasks(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'active_logistics_tasks'); }
function readTransferAttempts(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'economy_transfer_attempts'); }
function readTransferSuccesses(event: AnalyticsEventRecord): number | null { return readOptionalNumber(event, 'economy_transfer_successes'); }
function readTransferSuccessRate(event: AnalyticsEventRecord): number | null { const attempts = readTransferAttempts(event); const successes = readTransferSuccesses(event); return attempts === null || successes === null ? null : attempts === 0 ? 0 : (successes / attempts) * 100; }
function readOptionalNumber(event: AnalyticsEventRecord, key: string): number | null { const value = findProperty(event, key)?.value; return typeof value === 'number' && Number.isFinite(value) ? value : null; }
